import { TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { TonalBadge } from "../general/TonalBadge";
import { COLUMN_HEADING } from "../general/tableStyles";
import { Marked } from "../guide/GuideMarker";
import { Defendant } from "./IssueModel";

/** Wide enough for a win-chance slider with its figure, or a liability. */
export const DEFENDANT_COLUMN_MIN_WIDTH = 160;

/**
 * The heading of one part of a claim's table: what the rows are, one column
 * per defendant and, with several defendants, two columns that sum a row up
 * over all of them. The win chances and the claimed amounts of a claim share
 * the defendant columns, so they are headed alike.
 */
export const ClaimTableHead = ({
  label,
  firstColSpan = 1,
  defendants,
  summary,
  markers = {},
}: {
  label?: string;
  // the guide's entries of the column headings
  markers?: { label?: string; defendants?: string; summary?: string };
  // the rows of a claim with grouped issues start with a column to indent by
  firstColSpan?: number;
  defendants: Defendant[];
  summary?: { group: string; first: string; second: string };
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case.claims" });

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: "background.default" }}>
        <TableCell colSpan={firstColSpan} sx={COLUMN_HEADING}>
          <Marked entry={markers.label}>{label}</Marked>
        </TableCell>
        {defendants.map(({ id, name, settled }, index) => (
          <TableCell
            key={id}
            sx={{ minWidth: DEFENDANT_COLUMN_MIN_WIDTH, fontWeight: 700 }}
          >
            <Marked entry={index === 0 ? markers.defendants : undefined}>
              {name}
            </Marked>
            {settled && (
              <TonalBadge tone={theme.palette.warning} sx={{ ml: 1 }}>
                {t("settled")}
              </TonalBadge>
            )}
          </TableCell>
        ))}
        {summary &&
          [summary.first, summary.second].map((column, index) => (
            <TableCell key={column} align="right" sx={COLUMN_HEADING}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  textTransform: "none",
                  letterSpacing: "normal",
                  lineHeight: 1.3,
                }}
              >
                {summary.group}
              </Typography>
              <Marked entry={index === 0 ? markers.summary : undefined}>
                {column}
              </Marked>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
};
